import { useNavigate } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import { PHONE_MEDIA_QUERY, useMediaQuery } from '../../../common/responsive';
import { hapticsImpactLight } from '../../../common/haptic';
import { useDispatch, useSelector } from 'react-redux';
import { selectThemeMode } from '../../../redux/slices/themeSlice';
import Button from '../../../components/Button';
import { ParagraphMediumText } from '../../../components/Texts';
import InfoIcon from '../../../assets/icons/info.svg?react';
import { openModal } from '../../../redux/slices/modals';
import CashbackInfo from '../../../components/CashbackInfo';

const Container = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  margin-bottom: 8px;
  @media ${PHONE_MEDIA_QUERY} {
    gap: 8px;
  }
`;

const ScrollContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  &::-webkit-scrollbar {
    display: none;
  }
  scrollbar-width: none;
  -ms-overflow-style: none;
`;

const ScrollInner = styled.div`
  display: flex;
  padding-top: 5px;
  gap: 16px;
  width: fit-content;
  flex-wrap: nowrap;
  @media ${PHONE_MEDIA_QUERY} {
    padding-left: 0px;
    padding-right: 16px;
  }
`;

const ShopGiftCardContainer = styled.div`
  width: 100px;
  min-width: 100px;
  flex-shrink: 0;
  aspect-ratio: 1/1;
  border-radius: 8px;
  background-color: white;
  ${({ $isDark, theme }) =>
    !$isDark && `border: 1px solid ${theme.border.primary};`}
  background-image: url(${({ backgroundImage }) => backgroundImage});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  cursor: pointer;
  transition: transform 0.3s ease;
  @media (hover: hover) {
    &:hover {
      transform: translateY(-5px);
    }
  }
`;

const ShopGiftCard = ({ imageUrl, id, isDark }) => {
  const navigate = useNavigate();

  const handleClick = () => {
    hapticsImpactLight();
    navigate('/app/spend');
  };

  return (
    <ShopGiftCardContainer
      backgroundImage={imageUrl}
      $isDark={isDark}
      onClick={handleClick}
    />
  );
};

const ShopGiftCardsTitle = styled.p`
  font-weight: 500;
  font-size: 20px;
  color: ${({ theme }) => theme.text.title};
  cursor: pointer;
  @media ${PHONE_MEDIA_QUERY} {
    margin-top: 10px;
    font-size: 20px;
    font-weight: 500;
    color: ${({ theme }) => theme.text.title};
    cursor: pointer;
  }
`;

const ViewAllButton = styled.button`
  margin-top: 16px;
  padding: 12px 24px;
  background-color: ${({ theme }) => theme.background.secondary || '#f5f5f5'};
  border: 1px solid
    ${({ theme }) => theme.border.primary || 'rgba(0, 0, 0, 0.08)'};
  border-radius: 8px;
  color: ${({ theme }) => theme.text.title};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  width: 100%;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${({ theme }) => theme.background.lighter || '#e5e5e5'};
  }
`;

const ShopGiftCards = ({ showViewAllButton = false }) => {
  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);
  const navigate = useNavigate();
  const themeMode = useSelector(selectThemeMode);
  const theme = useTheme();
  const isDark = themeMode === 'dark';
  const dispatch = useDispatch();

  // Datos de ejemplo - puedes cambiarlos según necesites
  const shopGiftCards = [
    {
      id: 1,
      imageUrl: '/home/adidas.png',
    },
    {
      id: 2,
      imageUrl: '/home/nike.png',
    },
    {
      id: 3,
      imageUrl: '/home/airbnb.png',
    },
    {
      id: 4,
      imageUrl: '/home/playstation.png',
    },
    {
      id: 5,
      imageUrl: '/home/xbox.png',
    },
    {
      id: 6,
      imageUrl: '/home/amazon.png',
    },
    {
      id: 7,
      imageUrl: '/home/target.png',
    },
    {
      id: 8,
      imageUrl: '/home/apple.png',
    },
    {
      id: 9,
      imageUrl: '/home/doordash.png',
    },
    {
      id: 10,
      imageUrl: '/home/ebay.png',
    },
  ];

  return (
    <Container>
      <div
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          marginBottom: '8px',
        }}
      >
        <div>
          <ShopGiftCardsTitle
            onClick={() => {
              hapticsImpactLight();
              navigate('/app/spend');
            }}
          >
            Spend in shops{' '}
            <span style={{ marginLeft: '5px', fontSize: '24px' }}>{'›'}</span>
          </ShopGiftCardsTitle>
          <CashbackInfo />
        </div>
        {!isMobile && (
          <Button
            variant='secondary'
            style={{ height: '36px' }}
            onClick={() => {
              hapticsImpactLight();
              navigate('/app/spend');
            }}
          >
            View All Shops
          </Button>
        )}
      </div>
      <ScrollContainer>
        <ScrollInner>
          {shopGiftCards.map((card) => (
            <ShopGiftCard
              key={card.id}
              imageUrl={card.imageUrl}
              id={card.id}
              isDark={isDark}
            />
          ))}
        </ScrollInner>
      </ScrollContainer>
    </Container>
  );
};

export default ShopGiftCards;
